import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  Unsubscribe
} from 'firebase/firestore';
import { db } from './firebase';
import { School } from '../types';
import { sanitizeFirestoreData } from '../utils/sanitizeFirestore';

const COLLECTION_NAME = 'schools';

export function subscribeSchools(
  onData: (schools: School[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const schoolsRef = collection(db, COLLECTION_NAME);
  return onSnapshot(
    schoolsRef,
    (snapshot) => {
      const schools: School[] = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() as Omit<School, 'id'>)
      }));
      onData(schools);
    },
    (err) => {
      console.error('Error listening to schools collection:', err);
      if (onError) onError(err);
    }
  );
}

export async function addSchool(school: Omit<School, 'id'>): Promise<string> {
  const newRef = doc(collection(db, COLLECTION_NAME));
  const newSchool: School = {
    ...school,
    id: newRef.id
  };
  const sanitized = sanitizeFirestoreData(newSchool) as School;
  await setDoc(newRef, sanitized);
  return newRef.id;
}

export async function updateSchool(id: string, updates: Partial<School>): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const sanitized = sanitizeFirestoreData(updates);
  await updateDoc(docRef, sanitized);
}

export async function deleteSchool(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
}

export async function reorderSchools(orderedSchools: School[]): Promise<void> {
  const batch = writeBatch(db);
  orderedSchools.forEach((school, idx) => {
    const docRef = doc(db, COLLECTION_NAME, school.id);
    batch.update(docRef, { displayOrder: idx + 1 });
  });
  await batch.commit();
}

